import { Document, Page, pdf, StyleSheet, Text, View, Image, Font } from '@react-pdf/renderer';
import React, { useState } from 'react';
import { FileArrowDownIcon, SpinnerIcon } from '@phosphor-icons/react';
import { chatService } from '../../../Services/chatService';
import { RootState, useAppSelector } from '../../../store';
import useModal from '../../Modal/useModal';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeMinifyWhitespace from 'rehype-minify-whitespace';
import Html from 'react-pdf-html';
import ReactDOMServer from 'react-dom/server';

const styles = StyleSheet.create({
    // General styles
    page: {
        padding: '30px 30px 60px 50px',
    },
    imageLogo: {
        width: 40,
        height: 40,
        borderRadius: 100,
        marginRight: 10,
        marginLeft: -20,
    },
    chatHeader: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 10,
    },
    chatBody: {
        marginBottom: '60px',
    },
    chatMessageHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    chatImageSpace: {
        marginLeft: -16,
        marginRight: 10,
    },
    chatImage: {
        width: 32,
        height: 32,
        borderRadius: 100,
    },
    headerAbbreviation: {
        width: 40,
        height: 40,
        fontSize: 12,
        borderRadius: 100,
        marginRight: 10,
        marginLeft: -20,
        backgroundColor: '#f7f8f9',
        alignItems: 'center',
        justifyContent: 'center',
    },
    chatAbbreviation: {
        width: 32,
        height: 32,
        fontSize: 12,
        borderRadius: 100,
        backgroundColor: '#f7f8f9',
        alignItems: 'center',
        justifyContent: 'center',
    },
    chat: {
        marginLeft: 16,
        padding: 10,
    },
    role: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 5,
        alignItems: 'center'
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 30,
        right: 30,
        fontSize: 10,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    inline: {
        display: 'flex',
        flexDirection: 'row',
    },

    // Element styles
    h1: {
        fontFamily: 'Helvetica',
        fontSize: 16,
        fontWeight: 800,
        lineHeight: 1.4,
        color: '#000',
        marginTop: 0,
        marginBottom: 5,
    },
    h2: {
        fontFamily: 'Helvetica',
        fontSize: 14,
        fontWeight: 800,
        lineHeight: 1.4,
        color: '#000',
        marginTop: 0,
        marginBottom: 5,
    },
    h3: {
        fontFamily: 'Helvetica',
        fontSize: 13,
        fontWeight: 800,
        lineHeight: 1.4,
        color: '#000',
        marginTop: 0,
        marginBottom: 5,
    },
    h4: {
        fontFamily: 'Helvetica',
        fontSize: 12,
        fontWeight: 800,
        lineHeight: 1.4,
        color: '#000',
        marginTop: 0,
        marginBottom: 5,
    },
    h5: {
        fontFamily: 'Helvetica',
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1.4,
        color: '#000',
        marginTop: 0,
        marginBottom: 5,
    },
    h6: {
        fontFamily: 'Helvetica',
        fontSize: 10,
        fontWeight: 800,
        lineHeight: 1.4,
        color: '#000',
        marginTop: 0,
        marginBottom: 5,
    },
    a: {
        color: '#000',
    },
    p: {
        fontFamily: 'Helvetica',
        fontSize: 10,
        fontWeight: 500,
        lineHeight: 1.4,
        color: '#000',
        marginTop: 0,
        marginBottom: 5,
    },
    ul: {
        marginTop: 0,
        marginBottom: 5,
    },
    ol: {
        marginTop: 0,
        marginBottom: 5,
    },
    li: {
        fontFamily: 'Helvetica',
        fontSize: 10,
        fontWeight: 500,
        lineHeight: 1.4,
        color: '#000',
    },
    li_bullet: {
        textAlign: 'left',
        width: 20,
    },
    preCode: {
        backgroundColor: '#f7f8f9',
        border: '1px solid #e8ebec',
        borderRadius: 8,
        padding: 15,
        marginTop: 0,
        marginBottom: 10,
    },
    preCite: {
        borderLeft: '1px solid #e8ebec',
        paddingHorizontal: 15,
        marginTop: 0,
        marginBottom: 10,
    },
    pre: {
        fontSize: 10,
        fontFamily: 'Courier',
        lineHeight: 1.4,
    },
    dlpNote: {
        fontSize: 8,
        backgroundColor: '#60a5fa20',
        border: '1px solid #60a5fa',
        color: '#60a5fa',
        borderRadius: 6,
        padding: 10,
        marginTop: 0,
        marginBottom: 10,
    },
    documentsBox: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginBottom: 10,
    },
    documentFileBox: {
        border: '1px solid #e8ebec',
        backgroundColor: '#f7f8f9',
        borderRadius: 8,
        padding: 8,
        marginRight: 8,
        marginBottom: 8,
        alignSelf: 'flex-start',
    },
    documentFileName: {
        fontFamily: 'Helvetica',
        fontSize: 9,
        color: '#000',
    },
    table: {
        marginBottom: 10,
    },
    th: {
        borderBottom: '1px solid #e8ebec',
        borderRight: '1px solid #e8ebec',
        padding: 5,
    },
    tr: {
        fontSize: 10,
    },
    td: {
        borderRight: '1px solid #e8ebec',
        padding: 5,
    },
});

Font.registerEmojiSource({
    format: 'png',
    url: '/emoji/',
});

const markdownToHTML = (content: any) => {
    const html = (
        <ReactMarkdown
            remarkPlugins={[
                remarkGfm,
                remarkBreaks,
                [
                    remarkMath,
                    {
                        singleDollarTextMath: false,
                        doubleDollarTextMath: false,
                    },
                ],
            ]}
            rehypePlugins={[[rehypeKatex, { output: 'html' }], rehypeMinifyWhitespace]}
            urlTransform={(url: string) => {
                if (url.startsWith('data:')) {
                    return url;
                }
                return defaultUrlTransform(url);
            }}
            unwrapDisallowed={false}
            skipHtml={false}
            components={{
                pre({ children }) {
                    const child = React.Children.only(children) as React.ReactElement;
                    if (typeof child.type === 'function' && child.type.name === 'code') {
                        const { children, className } = child.props as {
                            children: React.ReactNode;
                            className?: string;
                        };

                        let language = (className || '').replace('language-', '');
                        let stringChildren = String(children)
                            .replace(/\n$/, '')
                            .replace(/###TRIPPLEBACKTICK###/g, '```');

                        const preStyle =
                            language === 'think' || language === 'error'
                                ? styles.preCite
                                : styles.preCode;

                        return (
                            <pre style={preStyle}>
                                {stringChildren}
                            </pre>
                        );
                    }

                    return <>{children}</>;
                },
                code({ node, className, children, ...props }) {
                    return (
                        <code {...props} className={className}>
                            {children}
                        </code>
                    );
                },
            }}
        >
            {content
                .replace(
                    /\\\[(.*?)\\\]/g,
                    (_: string, equation: string) => `$$${equation}$$`,
                )
                .replace(
                    /\\\((.*?)\\\)/g,
                    (_: string, equation: string) => `$${equation}$`,
                )
                .replace(/<error>(.*?)<\/error>/g, (_: any, p1: any) => {
                    return '```error\n' + p1.replace(/```/g, '###TRIPPLEBACKTICK###') + '\n```';
                })
                .replace('<error>', '```error')
                .replace('</error>', '```')
            }
        </ReactMarkdown>
    );
    return ReactDOMServer.renderToStaticMarkup(html);
}

const avatar = (role: string, userSettings: any, title: string, avatarImage: string) => {
    const avatarData =
        role === 'user'
            ? {
                  displayText: generateAbbreviation(userSettings.givenName + ' ' + userSettings.familyName),
                  isImageAvailable: Boolean(userSettings.image),
                  image: userSettings.image || '',
              }
            : {
                  displayText: generateAbbreviation(title || ''),
                  isImageAvailable: Boolean(avatarImage),
                  image: avatarImage || '',
              };

    function generateAbbreviation(name: string) {
        if (!name) return '';
        return name
            .split(' ')
            .map((word) => word[0])
            .join('')
            .substring(0, 2)
            .toUpperCase();
    }

    return avatarData.isImageAvailable ? (
        <Image src={avatarData.image} style={role === 'headerImage' ? styles.imageLogo : styles.chatImage} />
    ) : (
        <View style={role === 'headerImage' ? styles.headerAbbreviation : styles.chatAbbreviation}>
            <Text>{avatarData.displayText}</Text>
        </View>
    );
}

const createChatPDFDocument = (chat: any, userSettings: any, i18n: any) => (
    <Document>
        <Page wrap size="A4" style={styles.page}>
            <View style={styles.chatHeader}>
                {avatar('headerImage', userSettings, chat.prompt_name, chat.prompt_image)}
                <View>
                    <Text style={[styles.h1, { marginBottom: 0 }]}>
                        {chat.title.length > 50
                            ? chat.title.substring(0, 50) + ' ...'
                            : chat.title}
                    </Text>
                    <Text style={[styles.p, {color: '#7c858e'}]}>{chat.prompt_name}</Text>
                </View>
            </View>

            <View style={styles.chatBody}>
                {Object.entries(chat.messages).map(([messageId, message]) => {
                    let { role, content, contentChanged, files } = message as {
                        role: string;
                        content: string;
                        contentChanged: boolean;
                        files?: { name?: string; url?: string; mediaType?: string }[];
                    };

                    content = content.replace(/<(?:think|thought)>(.*?)<\/(?:think|thought)>/g, (_: string, p1: string) => {
                        if (!p1) return '```think\n```';
                        return '```think\n' + p1.replace(/```/g, '###TRIPPLEBACKTICK###') + '\n```';
                    });
                    content = content.replace(/<(?:think|thought)>/g, '```think');
                    content = content.replace(/<\/(?:think|thought)>/g, '```');
                    content = content.replace(/<error>(.*?)<\/error>/g, (_: string, p1: string) => {
                        if (!p1) return '```error\n```';
                        return '```error\n' + p1.replace(/```/g, '###TRIPPLEBACKTICK###') + '\n```';
                    });
                    content = content.replace('<error>', '```error');
                    content = content.replace('</error>', '```');
                    content = content.replace(/```reasoning/g, '```think');

                    if (!content.trim() && role !== 'user') return null;

                    let internalLinks: { source: string; fileUrl: string }[] = [];
                    const parts = content.match(/\\[sandbox\\|.*?\\]/g);
                    if (parts) {
                        parts.forEach((part) => {
                            const matchResult = part.match(/\\[sandbox\\|(.*?)\\|(.*?)\\]/);
                            if (matchResult) {
                                const [, source, fileUrl] = matchResult;
                                internalLinks.push({ source: source, fileUrl: fileUrl });
                            }
                            content = content.replace(part, '');
                        });
                    }

                    const responseParts = content.match(/\[sandbox\|.*?\]/g);
                    if (responseParts) {
                        responseParts.forEach((part) => {
                            const matchResult = part.match(/\[sandbox\|(.*?)\|(.*?)\]/);
                            if (matchResult) {
                                const [, source, fileUrl] = matchResult;
                                internalLinks.push({ source: source, fileUrl: fileUrl });
                            }
                            content = content.replace(part, '');
                        });
                    }

                    internalLinks.forEach((link) => {
                        content = content.replace(link.source, link.fileUrl);
                    });

                    return (
                        <View key={messageId}>
                            <View style={styles.chatMessageHeader}>
                                <View style={styles.chatImageSpace}>
                                    {avatar(role, userSettings, chat.prompt_name, chat.prompt_image)}
                                </View>
                                <Text style={styles.role}>
                                    {role === 'user' ? `${userSettings.givenName} ${userSettings.familyName}` : chat.prompt_name}
                                </Text>
                            </View>

                            <View style={styles.chat}>
                                {role === 'user' && files && files.length > 0 && (
                                    <View style={styles.documentsBox}>
                                        {files.map((file: { name?: string; url?: string; mediaType?: string; sha1?: string }, index: number) =>
                                            <View key={index} style={styles.documentFileBox}>
                                                <Text style={styles.documentFileName}>
                                                    {file.name || ''}
                                                </Text>
                                            </View>
                                        )}
                                    </View>
                                )}
                                <Html stylesheet={styles}>
                                    {markdownToHTML(content)}
                                </Html>
                                { contentChanged && (
                                    <View style={styles.inline}>
                                        <Text style={[styles.dlpNote, styles.inline]}>
                                            {i18n.translate({
                                                id: role === 'user' ? 'chatMessage.modifiedDLP' : 'chatMessage.assistentDLP.badge',
                                                sourceName: 'Chat',
                                            })}
                                        </Text>
                                    </View>
                                )}
                            </View>
                        </View>
                    );
                })}
            </View>

            <View fixed style={styles.footer}>
                <Text>{document.getElementById('root')?.dataset.projectDomain ?? 'varios-ai.com'}</Text>
                <Text render={({ pageNumber, totalPages }) => (
                    <>{`${pageNumber} | ${totalPages}`}</>
                )} />
            </View>
        </Page>
    </Document>
);

export default function ChatPDF({chatIdentifier}: {chatIdentifier: string}) {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const userSettings = useAppSelector((state: RootState) => state.userSettings);
    const modal = useModal();
    const [isDownloading, setIsDownloading] = useState(false);

    const handleExportClick = () => {
        modal.confirm({
            title: i18n.translate({ id: 'pdfExport.modal.title', sourceName: 'Chat' }),
            content: i18n.translate({ id: 'pdfExport.modal.content', sourceName: 'Chat' }),
            cancelText: i18n.translate({ id: 'pdfExport.modal.cancel', sourceName: 'Chat' }),
            confirmText: i18n.translate({ id: 'pdfExport.modal.confirm', sourceName: 'Chat' }),
            onConfirm: async () => {
                setIsDownloading(true);
                try {
                    await downloadChatPDF(chatIdentifier, userSettings, i18n);
                } finally {
                    setIsDownloading(false);
                }
            },
        });
    };

    return (
        <button
            className={`border-none text-chatlist-inlineButton-text bg-chatlist-inlineButton-background hover:bg-chatlist-inlineButton-background-hover rounded-full p-2 cursor-pointer hover:text-chatlist-inlineButton-text-hover md:group-hover:block `}
            onClick={handleExportClick}
            disabled={isDownloading}
        >
            {isDownloading ? <SpinnerIcon size={16} className="animate-spin" /> : <FileArrowDownIcon size={16} />}
        </button>
    );
}

export async function downloadChatPDF(chatIdentifier: string, userSettings: any, i18n: any) {
    const chat = (await chatService.getChat(chatIdentifier)).data;

    const blob = await pdf(createChatPDFDocument(chat, userSettings, i18n)).toBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.classList.add('hidden');
    let filename = chat.title.trim().replace(/[^\w\s-]/g, '').substring(0, 50);
    link.download = `${filename}.pdf`;

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
    }, 1000);
}
